// Content for /products ("Shop all products."). Copy fields hold i18next keys in the
// `products` namespace, not literal text; prices come from the embedded cart catalog.
// Mobile and desktop use different copy in Figma, so CTAs, row titles and descriptions
// carry a short (mobile) and a long (desktop) key.

import { imageUrl } from "~/lib/image-url";
import type { ProductSelection } from "~/lib/use-embedded-cart-global";

// Plain public/ paths, not JS imports — Picture needs real .avif/.webp siblings on disk.
// View and Go art is FPO in Figma; these are the closest existing renders until final art lands.
const driveBox = imageUrl("/images/products-list/drive-box.png");
const obdDevice = imageUrl("/images/products/nav-drive.png");
const obdFleetBox = imageUrl("/images/products/nav-drive-fleet.png");
const viewDashcam = imageUrl("/images/products/home-view-dashcam.png");
const goCharger = imageUrl("/images/products/nav-go.png");

export type ProductListCta =
  // "Add": adds `productCode` to the embedded cart and shows its live price. `selection` picks
  // the variant (keys and values are the catalog's attribute keys); any attribute it leaves out
  // takes its first value. Without a code the button renders as designed but does nothing yet
  // (see the TODO below).
  | { kind: "cart"; productCode?: string; selection?: ProductSelection }
  // A plain link, e.g. "Build Quote". `label` points at a { short, long, aria } key group.
  | { kind: "link"; href: string; label: "list.quote" };

export interface ProductListRow {
  /** Stable per-row id, used in data-sid. */
  id: string;
  /** Small blue label above the title on mobile (e.g. the connector, "J1772"). */
  eyebrow?: string;
  title: string;
  /** Mobile title; falls back to `title`. */
  shortTitle?: string;
  image: string;
  /** "cover" fills the thumbnail tile (Figma crops these); default "contain" shows the whole product. */
  imageFit?: "cover" | "contain";
  cta: ProductListCta;
}

export interface ProductListFamily {
  id: "drive" | "view" | "go";
  /** Embedded-cart product code; also picks the family's wordmark SVG. */
  productCode: string;
  name: string;
  description: string;
  shortDescription: string;
  image: string;
  imageAlt: string;
  /**
   * Rendered image width (px) per breakpoint. Figma scales each render past its frame and clips
   * it, so these reproduce its crops: the frame is 215x130 on mobile, 241x228 at md, 326x228 at lg
   * and 405x228 from xl.
   */
  imageWidths: { base: number; md: number; lg: number; xl: number };
  /** "top" pins the image just above the frame's top edge (Go's tall charger); default centers it. */
  imageAnchor?: "center" | "top";
  href: string;
  rows: ProductListRow[];
}

// TODO(BNC-5657): the dash cam and charger accessories aren't in the cart catalog yet, so their
// "Add" is inert and they show no price. Don't guess: an unknown code is refused by
// useAddToEmbeddedCart. Fill in `productCode` once they're added to the catalog.
export const PRODUCT_FAMILIES: ProductListFamily[] = [
  {
    id: "drive",
    productCode: "bouncieobd",
    name: "list.families.drive.name",
    description: "list.families.drive.description",
    shortDescription: "list.families.drive.shortDescription",
    image: driveBox,
    imageAlt: "list.families.drive.imageAlt",
    imageWidths: { base: 309, md: 422, lg: 528, xl: 551 },
    href: "/products/drive",
    rows: [
      {
        id: "obd2",
        title: "list.rows.obd2.title",
        image: obdDevice,
        imageFit: "cover",
        cta: { kind: "cart", productCode: "bouncieobd" },
      },
      {
        id: "obd2-fleets",
        title: "list.rows.obd2Fleets.title",
        image: obdFleetBox,
        imageFit: "cover",
        cta: { kind: "link", href: "/fleet/quote", label: "list.quote" },
      },
    ],
  },
  {
    id: "view",
    productCode: "bounciedashcam",
    name: "list.families.view.name",
    description: "list.families.view.description",
    shortDescription: "list.families.view.shortDescription",
    image: viewDashcam,
    imageAlt: "list.families.view.imageAlt",
    imageWidths: { base: 225, md: 284, lg: 326, xl: 405 },
    href: "/products/view",
    rows: [
      {
        id: "dual-view",
        title: "list.rows.dualView.title",
        shortTitle: "list.rows.dualView.shortTitle",
        image: viewDashcam,
        cta: { kind: "cart", productCode: "bounciedashcam" },
      },
      {
        id: "dash-cam-accessory",
        title: "list.rows.dashCamAccessory.title",
        shortTitle: "list.rows.dashCamAccessory.shortTitle",
        image: viewDashcam,
        cta: { kind: "cart" },
      },
    ],
  },
  {
    id: "go",
    productCode: "bounciecharger",
    name: "list.families.go.name",
    description: "list.families.go.description",
    shortDescription: "list.families.go.shortDescription",
    image: goCharger,
    imageAlt: "list.families.go.imageAlt",
    imageWidths: { base: 188, md: 319, lg: 326, xl: 345 },
    imageAnchor: "top",
    href: "/products/go",
    rows: [
      {
        id: "j1772-hardwired",
        eyebrow: "list.rows.j1772Hardwired.eyebrow",
        title: "list.rows.j1772Hardwired.title",
        image: goCharger,
        cta: {
          kind: "cart",
          productCode: "bounciecharger",
          selection: { "Connector Type": "J1772", "Power Regulation": "Hardwired" },
        },
      },
      {
        id: "j1772-nema",
        eyebrow: "list.rows.j1772Nema.eyebrow",
        title: "list.rows.j1772Nema.title",
        image: goCharger,
        cta: {
          kind: "cart",
          productCode: "bounciecharger",
          selection: { "Connector Type": "J1772", "Power Regulation": "NEMA" },
        },
      },
      {
        id: "nacs-hardwired",
        eyebrow: "list.rows.nacsHardwired.eyebrow",
        title: "list.rows.nacsHardwired.title",
        image: goCharger,
        cta: {
          kind: "cart",
          productCode: "bounciecharger",
          selection: { "Connector Type": "NACS", "Power Regulation": "Hardwired" },
        },
      },
      {
        id: "nacs-nema",
        eyebrow: "list.rows.nacsNema.eyebrow",
        title: "list.rows.nacsNema.title",
        image: goCharger,
        cta: {
          kind: "cart",
          productCode: "bounciecharger",
          selection: { "Connector Type": "NACS", "Power Regulation": "NEMA" },
        },
      },
      {
        id: "charger-accessory",
        title: "list.rows.chargerAccessory.title",
        shortTitle: "list.rows.chargerAccessory.shortTitle",
        image: goCharger,
        cta: { kind: "cart" },
      },
    ],
  },
];
